import { useEffect, useState } from "react";
import { Button, ButtonGroup, Col, Form, InputGroup, Row } from "react-bootstrap";
import { Search, XLg } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import { addFavouriteGame, deleteFavouriteGameByLink, fetchFavouriteGames } from "../../functions/favouriteGame";
import { fetchGames } from "../../functions/game";
import { createGameSession } from "../../functions/gameSession";
import Loader from "../Loader";
import GameSearchResultDisplayer from "./GameSearchResultDisplayer";
import FavouriteGameEditor from "../privatearea/game/favouritegame/edit/FavouriteGameEditor";
import GameSessionEditor from "../privatearea/gamesession/edit/GameSessionEditor";
import SuccessModal from "../SuccessModal";
import { fetchGameLabels, setGameLabels } from "../../functions/favouriteGameLabel";
import PageLayout from "../PageLayout";

const LoadingStatus = Object.freeze({
    'INIT': 'INIT',
    'LOADED': 'LOADED',
    'LOADING': 'LOADING',
});

function GameSearcher({ token, onError, onSearchProductClicked }) {
    const { t } = useTranslation('messages');

    const [games, setGames] = useState([]);
    const [lastId, setLastId] = useState("");
    const [favouriteGames, setFavouriteGames] = useState([]);
    const [loadingStatus, setLoadingStatus] = useState(LoadingStatus.INIT);
    const [query, setQuery] = useState("");
    const [latestQuery, setLatestQuery] = useState("");
    const [labels, setLabels] = useState([]);
    const [showEditGame, setShowEditGame] = useState(false);
    const [underEditGame, setUnderEditGame] = useState(null);
    const [underAssociationLabelsMap, setUnderAssociationLabelsMap] = useState(new Map());
    const [checkedLabelIds] = useState([]);
    const [gameType, setGameType] = useState("BOARDGAME");
    const [showCreateSession, setShowCreateSession] = useState(false);
    const [createSessionGame, setCreateSessionGame] = useState(null);
    const [showCreatedToast, setShowCreatedToast] = useState(false);

    useEffect(() => {
        fetchFavouriteGames(token, (data) => setFavouriteGames(data.gamesWithLabels), onError, {});
        fetchGameLabels(token, (data) => setLabels(data.labels), onError);
    }, [token, onError]);

    const doSearch = (e) => {
        e.preventDefault();
        onSearchGamesClicked(e, token, query, gameType, setGames, setLastId, setLatestQuery, setLoadingStatus, onError);
    };

    let afterSearchResult;
    if (loadingStatus === LoadingStatus.LOADING) {
        afterSearchResult = (
            <div className="text-center mt-4">
                <Loader />
            </div>
        );
    } else if (loadingStatus === LoadingStatus.LOADED && lastId && games.length > 0) {
        afterSearchResult = (
            <div className="text-center mt-4">
                <Button
                    variant="outline-secondary"
                    onClick={(e) => onLoadMoreResultsClicked(e, token, games, lastId, latestQuery, gameType, setGames, setLastId, setLoadingStatus, onError)}>
                    {t('main.game_search.result_load_more_button_text')}
                </Button>
            </div>
        );
    }

    return (
        <>
            <PageLayout
                hero={
                    <>
                        <h1 className="display-6 fw-bold mb-4">{t('main.game_search.title')}</h1>
                        <Row>
                            <Col xs={12} md={8} lg={6}>
                                <Form onSubmit={doSearch}>
                                    <InputGroup size="lg" className="mb-3">
                                        <Form.Control
                                            type="text"
                                            value={query}
                                            placeholder={t('main.game_search.search_form_placeholder')}
                                            onChange={e => setQuery(e.target.value)}
                                            className="border-0"
                                        />
                                        {query && (
                                            <Button variant="outline-light" onClick={() => setQuery("")} tabIndex={-1}>
                                                <XLg size={14} />
                                            </Button>
                                        )}
                                        <Button variant="light" type="submit" className="fw-semibold px-4">
                                            <Search size={14} className="me-2" />
                                            {t('main.game_search.search_form_button_text')}
                                        </Button>
                                    </InputGroup>
                                    <ButtonGroup>
                                        {[
                                            { value: 'BOARDGAME', label: t('main.game_search.game_type_boardgame') },
                                            { value: 'RPG',       label: t('main.game_search.game_type_rpg') },
                                            { value: 'VIDEOGAME', label: t('main.game_search.game_type_videogame') },
                                        ].map(({ value, label }) => (
                                            <Button
                                                key={value}
                                                variant={gameType === value ? 'light' : 'outline-light'}
                                                className="fw-semibold"
                                                onClick={() => setGameType(value)}>
                                                {label}
                                            </Button>
                                        ))}
                                    </ButtonGroup>
                                </Form>
                            </Col>
                        </Row>
                    </>
                }
            >
                {games.length > 0 && (
                    <p className="text-muted small mb-3">{t('main.game_search.powered_by')}</p>
                )}
                <GameSearchResultDisplayer
                    games={games}
                    hasSearched={loadingStatus !== LoadingStatus.INIT}
                    favouriteGames={favouriteGames}
                    onAddGameClicked={(game) => onAddGameClicked(token, game, addFavouriteGame, setFavouriteGames, onError)}
                    onDeleteGameClicked={(game) => onDeleteGameClicked(token, game, deleteFavouriteGameByLink, setFavouriteGames, onError)}
                    onSearchProductClicked={onSearchProductClicked}
                    onShowGameEditor={(game) => onShowGameEditor(game, setShowEditGame, setUnderEditGame, setUnderAssociationLabelsMap)}
                    onCreateGameSessionClicked={(game) => {
                        setCreateSessionGame(game);
                        setShowCreateSession(true);
                    }}
                />
                {afterSearchResult}
            </PageLayout>

            <FavouriteGameEditor
                gameLabels={labels}
                associateLabels={(gameId, labelIds) => onAssociateLabelsClicked(token, gameId, labelIds, checkedLabelIds, setLabels, setFavouriteGames, onError)}
                showEditGame={showEditGame}
                underAssociationLabelsMap={underAssociationLabelsMap}
                setUnderAssociationLabelsMap={setUnderAssociationLabelsMap}
                underEditGame={underEditGame}
                onCloseGameEditor={() => onCloseGameEditor(setShowEditGame, setUnderEditGame, setUnderAssociationLabelsMap)}
            />
            <GameSessionEditor
                show={showCreateSession}
                onClose={() => {
                    setShowCreateSession(false);
                    setCreateSessionGame(null);
                }}
                session={null}
                externalId={createSessionGame ? createSessionGame.externalId : null}
                gameName={createSessionGame ? createSessionGame.name : null}
                gameType={createSessionGame ? createSessionGame.gameType : null}
                onSave={(payload) => createGameSession(token, () => setShowCreatedToast(true), onError, payload)}
            />
            <SuccessModal
                show={showCreatedToast}
                message={t('game_sessions.created_toast')}
                onClose={() => setShowCreatedToast(false)}
            />
        </>
    );
}

function onAddGameClicked(token, game, addFavouriteGame, setFavouriteGames, onError) {
    addFavouriteGame(token, (data) => setFavouriteGames(data.games), onError, game);
}

function onDeleteGameClicked(token, game, deleteFavouriteGameByLink, setFavouriteGames, onError) {
    deleteFavouriteGameByLink(token, (data) => setFavouriteGames(data.games), onError, game);
}

function onSearchGamesClicked(e, token, searchValue, gameType, setGames, setLastId, setLatestQuery, setLoadingStatus, onError) {
    e.preventDefault();
    if (!searchValue) return;
    setGames([]);
    setLastId("");
    setLoadingStatus(LoadingStatus.LOADING);
    fetchGames(
        token,
        (data) => {
            setGames(data.games);
            setLastId(data.lastId);
            setLatestQuery(searchValue);
            setLoadingStatus(LoadingStatus.LOADED);
        },
        (errorCode, errorDescription) => {
            setGames([]);
            setLastId("");
            setLoadingStatus(LoadingStatus.LOADED);
            onError(errorCode, errorDescription);
        },
        searchValue, "", gameType);
}

function onLoadMoreResultsClicked(e, token, games, lastId, latestQuery, gameType, setGames, setLastId, setLoadingStatus, onError) {
    e.preventDefault();
    if (!lastId) return;
    setLoadingStatus(LoadingStatus.LOADING);
    fetchGames(
        token,
        (data) => {
            setGames([...games, ...data.games]);
            setLastId(data.lastId);
            setLoadingStatus(LoadingStatus.LOADED);
        },
        (errorCode, errorDescription) => {
            setGames([]);
            setLastId("");
            setLoadingStatus(LoadingStatus.LOADED);
            onError(errorCode, errorDescription);
        },
        latestQuery, lastId, gameType);
}

function fetchData(token, setFavouriteGames, setLabels, onError, checkedLabelIds) {
    fetchFavouriteGames(token, (data) => setFavouriteGames(data.gamesWithLabels), onError, { gameLabelIds: checkedLabelIds });
    fetchGameLabels(token, (data) => setLabels(data.labels), onError);
}

function onAssociateLabelsClicked(token, gameId, labelIds, checkedLabelIds, setLabels, setFavouriteGames, onError) {
    setGameLabels(token, () => fetchData(token, setFavouriteGames, setLabels, onError, checkedLabelIds), onError, gameId, labelIds);
}

function onShowGameEditor(game, setShowEditGame, setUnderEditGame, setUnderAssociationLabelsMap) {
    setShowEditGame(true);
    setUnderEditGame(game);
    const map = game.gameLabels.reduce((map, gameLabel) => {
        map.set(gameLabel.gameLabelId, true);
        return map;
    }, new Map());
    setUnderAssociationLabelsMap(map);
}

function onCloseGameEditor(setShowEditGame, setUnderEditGame, setUnderAssociationLabelsMap) {
    setShowEditGame(false);
    setUnderEditGame(null);
    setUnderAssociationLabelsMap(new Map());
}

export default GameSearcher;
